import { el, clear } from "../components/dom.js";
import { progressField, priceNoteField, loanField, startAgainButton } from "../components/tracking-fields.js";
import { reviewField, tagsField } from "../components/review-tags.js";
import { tagSuggestions } from "../utils/filters.js";
import { inTheNews } from "../components/in-the-news.js";
import { offerUndoDelete } from "../components/undo.js";
import { peopleRow } from "../components/people-row.js";
import { historySection } from "../components/history-section.js";
import {
	ENGAGEMENT_STATUSES,
	BOOK_OWNERSHIP_STATUSES,
	BOOK_FORMATS,
	TIMES_FINISHED,
	AWARDS,
	awardLabel,
	statusLabel,
} from "../components/status-badge.js";
import {
	getBookById,
	updateBook,
	deleteBook,
	setBookRating,
	setBookFormats,
	setBookTimesFinished,
	setBookAwards,
	setBookNote,
	setBookOwnedEditions,
	setBookCoverOverride,
	bookDisplayCover,
	backfillBookSummary,
	NOTE_MAX_LENGTH,
} from "../db/books.js";
import { ownedEditionsSection } from "../components/editions-picker.js";
import { getGroupsContainingItem, listGroups } from "../db/groups.js";
import { groupsSection } from "../components/groups-section.js";
import { getBook, getBookEditions, getBookSeriesForBook } from "../api/books.js";
import { listBooks } from "../db/books.js";
import { addSeriesBookToGroup } from "./book-series.js";
import { listPicker } from "../components/picker.js";
import { formatDate } from "../utils/dates.js";
import { navigate, navigateBack, rerenderRoute } from "../main.js";
import { openItemMenu } from "../components/item-menu.js";
import { overriddenFields } from "../utils/overrides.js";
import { statusSticker } from "../components/magazine/status.js";
import {
	magDetailHero,
	magSummary,
	magField,
	magSelectRow,
	magChips,
	magSingleChips,
	magRating,
	magNote,
	magDeleteFooter,
	magDateLine,
	magSeriesMore,
} from "../components/magazine/detail.js";

export async function renderBookDetail(root, id) {
	const book = await getBookById(id);
	if (!book) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Book not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/books") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);
	mount(view, book);

	// Lazy backfill of the work summary/subjects (search results omit them).
	if (book.summary == null && book.ol_id) {
		getBook(book.ol_id)
			.then(async (fresh) => {
				if (!fresh) return;
				const updated = await backfillBookSummary(book.id, {
					summary: fresh.summary ?? null,
					subjects: fresh.subjects ?? [],
				});
				if (updated && updated.summary !== book.summary) {
					mount(view, updated);
				}
			})
			.catch(() => {});
	}
}

async function mount(view, book) {
	// Re-mounting rebuilds the whole view. Capture scroll up-front, then do all the
	// async work WHILE THE OLD VIEW IS STILL ON SCREEN; only clear + append + restore
	// synchronously at the very end, so the browser never paints a cleared/top state
	// (that intermediate paint was the scroll jump, then the flash).
	const scrollY = window.scrollY;

	const groups = await getGroupsContainingItem(book);

	// ---- feature hero ----
	const kickerBits = ["Book"];
	if (book.published_year) kickerBits.push(String(book.published_year));
	if ((book.authors ?? []).length > 0) {
		kickerBits.push(book.authors.slice(0, 2).join(", "));
	}

	const figures = [
		{ value: book.rating != null ? book.rating : "—", label: "Rating" },
	];
	if ((book.subjects ?? []).length > 0) {
		figures.push({ value: book.subjects[0], label: "Subject" });
	} else if (book.published_year) {
		figures.push({ value: String(book.published_year), label: "Published" });
	}

	if (overriddenFields(book).length > 0) kickerBits.push("Edited");

	const hero = magDetailHero(book, {
		kicker: kickerBits.join(" · "),
		title: book.title,
		sticker: statusSticker(book.engagement_status, "book"),
		figures,
		tag: (book.awards ?? [])[0] ? awardLabel(book.awards[0]) : null,
		onBack: () => navigateBack("/books"),
		// "⋯": edit details / re-link / undo / reset (components/item-menu.js).
		onMenu: () =>
			openItemMenu({
				mediaType: "book",
				record: book,
				onChanged: (updated, { relinked } = {}) =>
					relinked ? rerenderRoute() : mount(view, updated),
			}),
	});

	// ---- fields ----
	const engagementField = magField(
		"Read it?",
		magSelectRow(
			ENGAGEMENT_STATUSES,
			book.engagement_status,
			async (value) => {
				mount(view, await updateBook(book.id, { engagement_status: value }));
			},
			(v) => statusLabel(v, "book"),
		),
		{ index: 0 },
	);

	const ownershipField = magField(
		"Got it?",
		magSelectRow(
			BOOK_OWNERSHIP_STATUSES,
			book.ownership_status ?? "have_it",
			async (value) => {
				mount(view, await updateBook(book.id, { ownership_status: value }));
			},
			(v) => statusLabel(v, "book"),
		),
		{ index: 1 },
	);

	// Format applies whenever you have a copy in hand — owned, formerly owned, or
	// borrowed/library (you don't own it, but it still has a tangible format).
	const hasCopy =
		book.ownership_status === "have_it" ||
		book.ownership_status === "had_it" ||
		book.ownership_status === "borrowed";

	const selectedFormats = new Set(book.formats ?? []);
	const formatField =
		hasCopy &&
		magField(
			"Format",
			magChips(
				BOOK_FORMATS,
				selectedFormats,
				async (next) => {
					mount(view, await setBookFormats(book.id, Array.from(next)));
				},
				(v) => statusLabel(v, "book"),
			),
			{ index: 2 },
		);

	// Editions you own — the specific publications, layered on top of the quick
	// Format chips. Same gate as Format: you only own a copy when you have/had/
	// borrowed it.
	const editionsField =
		hasCopy &&
		magField(
			"Editions you own",
			ownedEditionsSection({
				owned: book.owned_editions ?? [],
				loadPage: ({ offset }) =>
					getBookEditions(book.ol_id, { offset, limit: 50 }),
				describe: describeBookEdition,
				persist: (list) => setBookOwnedEditions(book.id, list),
			}),
			{ index: 1 },
		);

	// Cover picker: once you own editions with their own art, choose which cover
	// represents the book (or revert to the original). Hidden until there's a
	// choice to make.
	const coverField = renderCoverField(book, view);

	const ratingField = magField(
		"Rating",
		magRating(book.rating ?? null, async (value) => {
			mount(view, await setBookRating(book.id, value));
		}),
		{ index: 3 },
	);

	const selectedAwards = new Set(book.awards ?? []);
	const awardsField = magField(
		"Awards",
		magChips(
			AWARDS.map((a) => a.value),
			selectedAwards,
			async (next) => {
				mount(view, await setBookAwards(book.id, Array.from(next)));
			},
			awardLabel,
		),
		{ index: 4 },
	);

	// "Times read" only makes sense once you've actually finished it.
	const timesFinishedField =
		book.engagement_status === "done" &&
		magField(
			"Times read",
			magSingleChips(
				TIMES_FINISHED,
				book.times_finished ?? null,
				async (value) => {
					mount(view, await setBookTimesFinished(book.id, value));
				},
			),
			{ index: 2 },
		);

	const noteField = magField(
		"Notes",
		magNote(book.note ?? "", {
			maxLength: NOTE_MAX_LENGTH,
			placeholder: 'A quick note — e.g. "lent to Sam"',
			onSave: (v) => setBookNote(book.id, v),
		}),
		{ index: 5 },
	);

	const groupsField = magField(
		"Groups",
		await groupsSection(book, groups, async () =>
			mount(view, await refreshBook(book.id)),
		),
		{ index: 1 },
	);

	const dates = magDateLine([
		["Finished", formatDate(book.date_first_completed)],
		["Last read", formatDate(book.last_engaged_date)],
		["Added", formatDate(book.created_at)],
	]);

	const footer = magDeleteFooter("Delete from library", async () => {
		if (!confirm(`Delete "${book.title}" from your list?`)) return;
		await deleteBook(book.id);
		navigate("/books");
		offerUndoDelete("book", book, { reopenRoute: `/books/${book.id}` });
	});

	// "More in this series" — filled async (the book's Open Library id → its
	// Wikidata series → members). Best-effort; hidden when the book isn't in a
	// series Wikidata knows about.
	const seriesMore = el("div", {});
	if (book.ol_id) {
		(async () => {
			try {
				const series = await getBookSeriesForBook(book.ol_id);
				if (!series.wd_id) return;
				const have = new Set((await listBooks()).map((b) => (b.title ?? "").trim().toLowerCase()));
				const siblings = (series.books ?? []).filter(
					(b) => (b.title ?? "").trim().toLowerCase() !== (book.title ?? "").trim().toLowerCase(),
				);
				if (siblings.length === 0) return;
				const entries = siblings.map((b) => ({
					title: b.title, cover_url: b.cover_url, year: b.published_year,
					owned: have.has((b.title ?? "").trim().toLowerCase()), raw: b,
				}));
				const meta = { wd_id: series.wd_id, title: series.title };
				clear(seriesMore);
				seriesMore.append(magSeriesMore("book", {
					entries,
					onAdd: async (e) => {
						await addSeriesBookToGroup(meta, e.raw);
						mount(view, await getBookById(book.id));
					},
					onAddAll: async () => {
						for (const b of series.books ?? []) await addSeriesBookToGroup(meta, b);
						const grp = (await listGroups()).find(
							(x) => x.media_type === "book" && (x.name ?? "").toLowerCase() === (series.title ?? "").toLowerCase(),
						);
						if (grp) navigate(`/groups/${grp.id}`);
						else mount(view, await getBookById(book.id));
					},
				}));
			} catch {
				// best-effort
			}
		})();
	}

	clear(view);
	view.append(
		hero,
		magSummary(book.summary) || "",
		inTheNews(book),
		peopleRow("book", book.ol_id),
		engagementField,
		ownershipField,
		formatField || "",
		editionsField || "",
		coverField,
		ratingField,
		awardsField,
		timesFinishedField || "",
		noteField,
		tagsField("book", book, { suggestions: tagSuggestions("book") }),
		reviewField("book", book),
		progressField("book", book),
		priceNoteField("book", book),
		loanField("book", book),
		startAgainButton("book", book),
		groupsField,
		seriesMore,
		dates,
		historySection(book),
		footer,
	);

	// Restore synchronously — in the same tick as the rebuild, so the browser never
	// paints the cleared/top state (a deferred rAF restore caused a visible flash).
	window.scrollTo(0, scrollY);
}

async function refreshBook(id) {
	return getBookById(id);
}

// Cover picker for a book: choose between the original source cover and any
// owned edition's art. Returns "" (renders nothing) until there's at least one
// owned edition with a cover to choose from.
function renderCoverField(book, view) {
	const editionsWithCover = (book.owned_editions ?? []).filter(
		(e) => e.cover_url,
	);
	if (editionsWithCover.length === 0) return "";

	const ORIGINAL = "__original__";
	const options = [
		// The source cover: parked in source_values once a different cover is chosen.
		{ id: ORIGINAL, url: book.source_values?.cover_url ?? book.cover_url ?? null, label: "Original cover" },
		...editionsWithCover.map((e) => ({
			id: e.edition_id,
			url: e.cover_url,
			label: describeBookEdition(e).title,
		})),
	];
	const byId = new Map(options.map((o) => [o.id, o]));
	const currentUrl = bookDisplayCover(book);
	const currentLabel =
		options.find((o) => o.url === currentUrl)?.label ?? "Original cover";

	const picker = listPicker({
		items: options.map((o) => ({
			id: o.id,
			name: o.label,
			subtitle: o.id === ORIGINAL ? "Source cover" : "Owned edition",
		})),
		placeholder: "Choose a cover…",
		emptyText: "No covers to choose from.",
		onPick: async (item) => {
			const url = item.id === ORIGINAL ? null : (byId.get(item.id)?.url ?? null);
			mount(view, await setBookCoverOverride(book.id, url));
		},
	});

	const toggleBtn = el(
		"button.btn.btn--small",
		{
			type: "button",
			onclick: () => {
				picker.hidden = !picker.hidden;
				if (!picker.hidden) {
					const input = picker.querySelector(".picker__input");
					if (input) queueMicrotask(() => input.focus());
				}
			},
		},
		"Change cover",
	);

	return magField(
		"Cover",
		el(
			"div",
			{},
			el("div.detail__hint", {}, el("span", {}, `Using: ${currentLabel}`)),
			toggleBtn,
			picker,
		),
		{ index: 2 },
	);
}

// Render a book edition (API result or stored snapshot — same fields) into the
// { title, sub, cover_url } shape the editions picker/chips use. Primary line is
// publisher + year (the most identifying bits); the sub line adds format + ISBN.
function describeBookEdition(e) {
	const primary = [e.publishers?.[0], e.publish_year].filter(Boolean);
	const title = primary.length ? primary.join(" · ") : e.title || "Edition";
	const sub = [e.physical_format, e.isbn].filter(Boolean).join(" · ") || null;
	return { title, sub, cover_url: e.cover_url ?? null };
}
